/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { InputFieldset } from "ui/inputFieldset";
import { SelectFieldset } from "ui/selectFieldset";
import { SelectTriggerUI } from "ui/selectTrigger";
import { Label } from "../../../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { Checkbox } from "../../../ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import { ArrowLeft, Plus, Trash2, Anchor } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "ui/select";
import { Alert, AlertDescription } from "../../../ui/alert";
import {
  createPortWithBlocks,
  getPortById,
  getPorts,
  updatePort,
  deletePortBlock,
} from "../../../services/portService";
import { mapTypeToApi, mapTypeFromApi } from "../../../utils/portTypeMapper";
import { removeSpecialCharacters } from "utils/commonutils";
import { usePageHeader } from "contexts/PageHeaderContext";

interface BlockedDate {
  id: string;
  portBlockId?: number;
  fromDate: string;
  toDate: string;
  fromTime: string;
  toTime: string;
  reason: string;
}

type PortType = "Air" | "Sea" | "Road";

interface Port {
  id: string;
  publicId: string;
  portName: string;
  portNameArabic: string;
  portShortName: string;
  arrivalShortName: string;
  departureShortName: string;
  isActive: boolean;
  blockedDates: BlockedDate[];
  type: PortType;
}

interface PortMasterFormPageProps {
  onBack: () => void;
}

export const PortMasterFormPage = ({ onBack }: PortMasterFormPageProps) => {
  const { publicId } = useParams<{ publicId: string }>();
  const navigate = useNavigate();
  const { setPageHeader } = usePageHeader();

  const mode = publicId ? "edit" : "add";
  const [showBlockDateDialog, setShowBlockDateDialog] = useState(false);
  const [showDeleteBlockDialog, setShowDeleteBlockDialog] = useState(false);
  const [blockDateToDelete, setBlockDateToDelete] =
    useState<BlockedDate | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [existingPorts, setExistingPorts] = useState<any[]>([]);
  const [newBlockDate, setNewBlockDate] = useState({
    fromDate: "",
    toDate: "",
    fromTime: "",
    toTime: "",
    reason: "",
  });
  const isViewMode = false;

  const [formData, setFormData] = useState<Port>({
    id: "",
    publicId: "",
    portName: "",
    portNameArabic: "",
    portShortName: "",
    arrivalShortName: "",
    departureShortName: "",
    isActive: true,
    blockedDates: [],
    type: "Air",
  });

  const loadPorts = async () => {
    try {
      const res = await getPorts({
        pageNo: 1,
        pageSize: 1000,
      });

      setExistingPorts(res.items);
    } catch (error) {
      console.error("Failed to load ports");
    }
  };

  useEffect(() => {
    setPageHeader({
      title: mode === "edit" ? "Edit Port" : "Add New Port",
      icon: <Anchor className="h-4 w-4" />,
      onBack: () => navigate("/port-master"),
    });

    return () => setPageHeader(null);
  }, [mode, navigate, setPageHeader]);

  useEffect(() => {
    loadPorts();
  }, []);

  const loadPort = async (id: string) => {
    try {
      const data = await getPortById(id);

      setFormData({
        id: data.portId.toString(),
        publicId: data.publicId,
        portName: data.portName,
        portNameArabic: data.portNameArabic,
        portShortName: data.portShortName,
        arrivalShortName: data.arrivalShortName,
        departureShortName: data.departureShortName,
        isActive: data.isActive,
        type: mapTypeFromApi(data.portType),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        blockedDates: data.blocks.map((b: any) => ({
          id: b.portBlockId.toString(),
          portBlockId: b.portBlockId,
          fromDate: b.fromDate.split("T")[0],
          toDate: b.toDate.split("T")[0],
          fromTime: b.fromTime.substring(0, 5),
          toTime: b.toTime.substring(0, 5),
          reason: b.reason,
        })),
      });
    } catch {
      toast.error("Failed to load port");
    }
  };

  useEffect(() => {
    if (publicId) {
      loadPort(publicId);
    }
  }, [publicId]);

  const [errors, setErrors] = useState<{
    portName?: string;
    portShortName?: string;
  }>({});

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleInputChange = (field: keyof Port, value: any) => {
    if (isViewMode) return;

    setFormData((prev) => ({ ...prev, [field]: value }));

    setErrors((prev) => ({
      ...prev,
      [field]: "",
    }));
  };

  const resetBlockForm = () => {
    setNewBlockDate({
      fromDate: "",
      toDate: "",
      fromTime: "",
      toTime: "",
      reason: "",
    });

    setShowBlockDateDialog(false);
  };

  const formatLocalDateTime = (date: string, time: string) => {
    return `${date}T${time || "00:00"}:00`;
  };

  const formatDisplayDate = (date: string) => {
    if (!date) return "";

    const [year, month, day] = date.split("-").map(Number);

    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(new Date(year, month - 1, day));
  };

  const formatBlockPayload = (block: {
    fromDate: string;
    toDate: string;
    fromTime?: string;
    toTime?: string;
    reason: string;
  }) => ({
    fromDate: formatLocalDateTime(block.fromDate, block.fromTime || "00:00"),
    toDate: formatLocalDateTime(block.toDate, block.toTime || "23:59"),
    fromTime:
      block.fromTime && block.fromTime !== ""
        ? `${block.fromTime}:00`
        : "00:00:00",
    toTime:
      block.toTime && block.toTime !== "" ? `${block.toTime}:00` : "23:59:59",
    reason: block.reason ?? "",
  });

  const handleSave = async () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const validationErrors: any = {};

    if (!formData.portName.trim()) {
      validationErrors.portName = "Port Name is required";
    }

    if (!formData.portShortName.trim()) {
      validationErrors.portShortName = "Port Short Name is required";
    }

    if (existingPorts.length > 0) {
      const duplicateName = existingPorts.find(
        (p) =>
          p.portName?.toLowerCase() === formData.portName.toLowerCase() &&
          p.portId !== Number(formData.id),
      );

      const duplicateShort = existingPorts.find(
        (p) =>
          p.portShortName.toLowerCase() ===
            formData.portShortName.toLowerCase() &&
          p.portId !== Number(formData.id),
      );

      if (duplicateName) validationErrors.portName = "Port name already exists";
      if (duplicateShort)
        validationErrors.portShortName = "Port short name already exists";
    }

    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }

    try {
      if (mode === "add") {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const payload: any = {
          portName: formData.portName,
          portNameArabic: formData.portNameArabic,
          portShortName: formData.portShortName,
          arrivalShortName: formData.arrivalShortName,
          departureShortName: formData.departureShortName,
          isActive: formData.isActive,
          portType: mapTypeToApi(formData.type),

          blocks: formData.blockedDates.map((b) => formatBlockPayload(b)),
        };

        await createPortWithBlocks(payload);

        toast.success("Port created successfully");
        onBack();
      }

      if (mode === "edit") {
        await updatePort(formData.publicId, {
          portId: Number(formData.id),
          publicId: formData.publicId,
          portName: formData.portName,
          portNameArabic: formData.portNameArabic,
          portShortName: formData.portShortName,
          arrivalShortName: formData.arrivalShortName,
          departureShortName: formData.departureShortName,
          portType: mapTypeToApi(formData.type),
          isActive: formData.isActive,

          blocks: formData.blockedDates.map((block) => ({
            portBlockId: block.portBlockId ?? 0,
            fromDate: formatLocalDateTime(
              block.fromDate,
              block.fromTime || "00:00",
            ),
            toDate: formatLocalDateTime(block.toDate, block.toTime || "23:59"),
            fromTime: block.fromTime ? `${block.fromTime}:00` : "00:00:00",

            toTime: block.toTime ? `${block.toTime}:00` : "23:59:59",
            reason: block.reason ?? "",
          })),

          deletedBy: 0,
          deletedAt: new Date().toISOString(),
        });

        toast.success("Port updated successfully");

        onBack();
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to save port");
    }
  };

  const handleAddBlockDate = () => {
    if (
      !newBlockDate.fromDate ||
      !newBlockDate.toDate ||
      !newBlockDate.reason
    ) {
      toast.error("Please fill in all block date fields");
      return;
    }

    const newBlock: BlockedDate = {
      id: `new-${Date.now()}`,
      ...newBlockDate,
    };

    setFormData((prev) => ({
      ...prev,
      blockedDates: [...prev.blockedDates, newBlock],
    }));

    toast.success("Block date added successfully");

    setNewBlockDate({
      fromDate: "",
      toDate: "",
      fromTime: "",
      toTime: "",
      reason: "",
    });

    setShowBlockDateDialog(false);
  };

  const handleRemoveBlockDate = (id: string) => {
    if (isViewMode) return;

    const block = formData.blockedDates.find((b) => b.id === id);

    if (!block) return;

    setBlockDateToDelete(block);
    setShowDeleteBlockDialog(true);
  };

  const confirmDeleteBlockDate = async () => {
    if (!blockDateToDelete) return;
    try {
      if (!blockDateToDelete.portBlockId) {
        setFormData((prev) => ({
          ...prev,
          blockedDates: prev.blockedDates.filter(
            (b) => b.id !== blockDateToDelete.id,
          ),
        }));

        toast.success("Block date removed successfully");
      } else if (mode === "edit") {
        await deletePortBlock(formData.publicId, blockDateToDelete.portBlockId);

        await loadPort(formData.publicId);

        toast.success("Block date removed successfully");
      } else {
        setFormData((prev) => ({
          ...prev,
          blockedDates: prev.blockedDates.filter(
            (b) => b.id !== blockDateToDelete.id,
          ),
        }));

        toast.success("Block date removed successfully");
      }

      setShowDeleteBlockDialog(false);
      setBlockDateToDelete(null);
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete block date");
    }
  };

  const getTitle = () => {
    switch (mode) {
      case "add":
        return "Add New Port";
      case "edit":
        return "Edit Port";
      default:
        return "Port Form";
    }
  };

  return (
    <div className="min-h-screen">
      <div className="max-w-[1400px] mx-auto">
        <div className="space-y-4 pb-6">
          {/* Header */}
          {/* <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-2">
              <Button
                onClick={onBack}
                variant="ghost"
                size="sm"
                className="text-[#5B9BD5] hover:bg-[#5B9BD5]/10 -ml-2"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1
                  style={{ color: "#5B9BD5" }}
                  className="flex items-center gap-2"
                >
                  <Anchor className="w-6 h-6" />
                  {getTitle()}
                </h1>
                <p className="text-gray-600 mt-1 text-sm">
                  {isViewMode
                    ? "Review port information"
                    : "Fill in the port details below"}
                </p>
              </div>
            </div>
          </div> */}

          {/* View Mode Alert */}
          {isViewMode && (
            <Alert className="bg-[#5B9BD5]/10 border-[#5B9BD5]/20">
              <AlertDescription className="text-gray-900 text-sm">
                You are in view-only mode. Click the back button to return or
                use Edit to make changes.
              </AlertDescription>
            </Alert>
          )}

          {/* Form */}
          <Card
            className="bg-white border-[#5B9BD5]/20 shadow-md"
            style={{ gap: "0px" }}
          >
            <CardHeader
              style={{
                backgroundColor: "#E3F2FD",
                padding: "16px",
                gap: "0px",
              }}
            >
              <CardTitle className="text-gray-900 text-base">
                Port Information
              </CardTitle>
              <p className="text-xs text-gray-600 mt-1">
                Configure port basic details and settings
              </p>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              <div className="space-y-6">
                {/* Row 1: Port Names */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Port Name */}
                  <div className="space-y-2">
                    <InputFieldset
                      label="Port Name"
                      required
                      id="portName"
                      value={formData.portName}
                      onChange={(e) => {
                        const value = removeSpecialCharacters(e.target.value);
                        handleInputChange("portName", value);
                      }}
                      placeholder="e.g., Baghdad Airport"
                      className={`bg-white ${
                        errors.portName
                          ? "border-red-500 focus:border-red-500"
                          : "border-gray-300"
                      }`}
                      disabled={isViewMode}
                    />

                    {errors.portName && (
                      <p className="text-red-500 text-xs">{errors.portName}</p>
                    )}
                  </div>

                  {/* Port Name (Arabic) */}
                  <div className="space-y-2">
                    <InputFieldset
                      label="Port Name (Arabic)"
                      id="portNameArabic"
                      value={formData.portNameArabic}
                      onChange={(e) => {
                        const value = removeSpecialCharacters(e.target.value);
                        handleInputChange("portNameArabic", value);
                      }}
                      placeholder="مطار بغداد"
                      className="text-right bg-white border-gray-300"
                      dir="rtl"
                      disabled={isViewMode}
                    />
                  </div>

                  {/* Port Short Name */}
                  <div className="space-y-2">
                    <InputFieldset
                      label=" Port Short Name"
                      required
                      id="portShortName"
                      value={formData.portShortName}
                      onChange={(e) => {
                        handleInputChange(
                          "portShortName",
                          e.target.value.toUpperCase(),
                        );
                      }}
                      placeholder="B"
                      maxLength={3}
                      className={`bg-white uppercase ${
                        errors.portShortName
                          ? "border-red-500 focus:border-red-500"
                          : "border-gray-300"
                      }`}
                      disabled={isViewMode}
                    />

                    {errors.portShortName && (
                      <p className="text-red-500 text-xs">
                        {errors.portShortName}
                      </p>
                    )}
                  </div>
                </div>

                {/* Row 2: Arrival & Departure Short Names + Status */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {/* Arrival Short Name */}
                  <div className="space-y-2">
                    <InputFieldset
                      label="Arrival Short Name"
                      id="arrivalShortName"
                      value={formData.arrivalShortName}
                      onChange={(e) => {
                        handleInputChange("arrivalShortName", e.target.value);
                      }}
                      placeholder="ARR-B"
                      className="bg-white border-gray-300"
                      disabled={isViewMode}
                    />
                  </div>

                  {/* Departure Short Name */}
                  <div className="space-y-2">
                    <InputFieldset
                      label="Departure Short Name"
                      id="departureShortName"
                      value={formData.departureShortName}
                      onChange={(e) => {
                        handleInputChange("departureShortName", e.target.value);
                      }}
                      placeholder="DEP-B"
                      className="bg-white border-gray-300"
                      disabled={isViewMode}
                    />
                  </div>

                  {/* Is Active Status */}
                  <div className="space-y-2">
                    <SelectFieldset label="Status">
                      <div className="h-6 flex items-center">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="isActive"
                            checked={formData.isActive}
                            onCheckedChange={(checked) =>
                              handleInputChange("isActive", checked)
                            }
                            disabled={isViewMode}
                            className="border-gray-300"
                          />
                          <Label
                            htmlFor="isActive"
                            className="text-sm text-gray-900 cursor-pointer font-normal"
                          >
                            Is Active
                          </Label>
                        </div>
                      </div>
                    </SelectFieldset>
                  </div>
                  <div className="space-y-2">
                    <SelectFieldset label="Type">
                      <Select
                        value={formData.type}
                        onValueChange={(value: "Air" | "Sea" | "Road") =>
                          handleInputChange("type", value)
                        }
                      >
                        <SelectTriggerUI>
                          <SelectValue placeholder="Select type" />
                        </SelectTriggerUI>

                        <SelectContent>
                          <SelectItem value="Air">Air</SelectItem>
                          <SelectItem value="Sea">Sea</SelectItem>
                          <SelectItem value="Road">Road</SelectItem>
                        </SelectContent>
                      </Select>
                    </SelectFieldset>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Block Dates Section */}
          <Card
            className="bg-white border-[#5B9BD5]/20 shadow-md"
            style={{ gap: "0px" }}
          >
            <CardHeader
              style={{
                backgroundColor: "#E3F2FD",
                padding: "16px",
                gap: "0px",
              }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-gray-900 text-base">
                    Block Dates
                  </CardTitle>
                  <p className="text-xs text-gray-600 mt-1">
                    Manage port blocking schedules
                  </p>
                </div>
                {!isViewMode && (
                  <Button
                    type="button"
                    size="sm"
                    className="text-white h-8"
                    style={{ backgroundColor: "#5B9BD5" }}
                    onClick={() => setShowBlockDateDialog(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Block Date
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent style={{ padding: "16px" }}>
              {formData.blockedDates.length > 0 ? (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                        <TableHead className="text-gray-900 text-xs py-2">
                          From Date
                        </TableHead>
                        <TableHead className="text-gray-900 text-xs py-2">
                          From Time
                        </TableHead>
                        <TableHead className="text-gray-900 text-xs py-2">
                          To Date
                        </TableHead>
                        <TableHead className="text-gray-900 text-xs py-2">
                          To Time
                        </TableHead>
                        <TableHead className="text-gray-900 text-xs py-2">
                          Reason
                        </TableHead>
                        {!isViewMode && (
                          <TableHead className="text-gray-900 text-xs py-2 text-right">
                            Action
                          </TableHead>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {formData.blockedDates.map((blockDate) => (
                        <TableRow key={blockDate.id}>
                          <TableCell className="text-gray-900 text-sm py-2">
                            {formatDisplayDate(blockDate.fromDate)}
                          </TableCell>
                          <TableCell className="text-gray-900 text-sm py-2">
                            {blockDate.fromTime || "N/A"}
                          </TableCell>
                          <TableCell className="text-gray-900 text-sm py-2">
                            {formatDisplayDate(blockDate.toDate)}
                          </TableCell>
                          <TableCell className="text-gray-900 text-sm py-2">
                            {blockDate.toTime || "N/A"}
                          </TableCell>
                          <TableCell className="text-gray-900 text-sm py-2">
                            {blockDate.reason}
                          </TableCell>
                          {!isViewMode && (
                            <TableCell className="text-right py-2">
                              <div className="flex justify-end gap-2">
                                {/* Delete */}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    handleRemoveBlockDate(blockDate.id)
                                  }
                                  className="text-red-600 hover:text-red-700 hover:bg-red-100 h-7 w-7 p-0"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className="text-center text-gray-400 py-6 text-sm">
                  No blocked dates configured
                </p>
              )}
            </CardContent>
          </Card>

          {/* Action Buttons */}
          {!isViewMode && (
            <div className="flex gap-3">
              <Button
                type="button"
                onClick={handleSave}
                className="text-white"
                style={{ backgroundColor: "#5B9BD5" }}
              >
                {mode === "edit" ? "Update Port" : "Save Port"}
              </Button>
              <Button
                variant="outline"
                onClick={onBack}
                className="border-[#5B9BD5] text-[#5B9BD5] hover:bg-[#5B9BD5]/10"
              >
                Cancel
              </Button>
            </div>
          )}

          {/* Add Block Date Dialog */}
          <Dialog
            open={showBlockDateDialog}
            onOpenChange={setShowBlockDateDialog}
          >
            <DialogContent className="bg-white sm:max-w-[550px]">
              <DialogHeader>
                <DialogTitle style={{ color: "#5B9BD5" }}>
                  Add Block Date
                </DialogTitle>
                <DialogDescription className="text-sm">
                  Configure date and time range to block this port
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                {/* Date Range */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <InputFieldset
                      label="From Date"
                      required
                      id="fromDate"
                      type="date"
                      value={newBlockDate.fromDate}
                      onChange={(e) =>
                        setNewBlockDate((prev) => ({
                          ...prev,
                          fromDate: e.target.value,
                        }))
                      }
                      className="border-gray-300"
                    />
                  </div>
                  <div className="space-y-2">
                    <InputFieldset
                      label="To Date"
                      required
                      id="toDate"
                      type="date"
                      value={newBlockDate.toDate}
                      onChange={(e) =>
                        setNewBlockDate((prev) => ({
                          ...prev,
                          toDate: e.target.value,
                        }))
                      }
                      className="border-gray-300"
                    />
                  </div>
                </div>

                {/* Time Range */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <InputFieldset
                      label="From Time"
                      id="fromTime"
                      type="time"
                      value={newBlockDate.fromTime}
                      onChange={(e) =>
                        setNewBlockDate((prev) => ({
                          ...prev,
                          fromTime: e.target.value,
                        }))
                      }
                      className="border-gray-300"
                    />
                  </div>
                  <div className="space-y-2">
                    <InputFieldset
                      label="To Time"
                      id="toTime"
                      type="time"
                      value={newBlockDate.toTime}
                      onChange={(e) =>
                        setNewBlockDate((prev) => ({
                          ...prev,
                          toTime: e.target.value,
                        }))
                      }
                      className="border-gray-300"
                    />
                  </div>
                </div>

                {/* Reason */}
                <div className="space-y-2">
                  <InputFieldset
                    label="Reason"
                    id="reason"
                    required
                    value={newBlockDate.reason}
                    onChange={(e) =>
                      setNewBlockDate((prev) => ({
                        ...prev,
                        reason: e.target.value,
                      }))
                    }
                    placeholder="Enter reason for blocking"
                    className="border-gray-300"
                  />
                </div>
              </div>
              <DialogFooter className="gap-4">
                <Button
                  variant="outline"
                  onClick={() => setShowBlockDateDialog(false)}
                  className="border-gray-300"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddBlockDate}
                  disabled={
                    !newBlockDate.fromDate ||
                    !newBlockDate.toDate ||
                    !newBlockDate.reason
                  }
                  className="text-white"
                  style={{ backgroundColor: "#5B9BD5" }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Block Date
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Delete Block Date Dialog */}
          <Dialog
            open={showDeleteBlockDialog}
            onOpenChange={(open) => {
              setShowDeleteBlockDialog(open);

              if (!open) {
                setBlockDateToDelete(null);
              }
            }}
          >
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle className="text-red-600">
                  Delete Block Date
                </DialogTitle>

                <DialogDescription>
                  Are you sure you want to delete this block date from{" "}
                  <span className="font-semibold">
                    "{formatDisplayDate(blockDateToDelete?.fromDate || "")}"
                  </span>{" "}
                  to{" "}
                  <span className="font-semibold">
                    "{formatDisplayDate(blockDateToDelete?.toDate || "")}"
                  </span>
                  ? This action cannot be undone.
                </DialogDescription>
              </DialogHeader>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDeleteBlockDialog(false);
                    setBlockDateToDelete(null);
                  }}
                >
                  No, Cancel
                </Button>

                <Button
                  onClick={confirmDeleteBlockDate}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Yes, Delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
};
